import { useI18n } from "@core/i18n/i18n";
import { useNavigate } from "@core/routing/use-navigate";
import { useQueryParams } from "@core/routing/use-query-params";
import { Patient } from "@domain/patient/patient";
import { AllPatientPrograms, EndedProgram, OngoingProgram, PatientProgram } from "@domain/program/patient-program";
import {
	AnalyticsProgramLocation,
	sendAnalyticsEventExtendProgram,
	sendAnalyticsEventViewProgram,
} from "@domain/program/program-analytics";
import { ProgramStatus } from "@domain/program/program-status/program-status";
import styled from "@emotion/styled";
import { AlertCard, CardType } from "@ui/components/common/alert-card";
import { Button } from "@ui/components/buttons/button";
import { ConditionalSkeleton } from "@ui/components/common/conditional-skeleton";
import { NetworkError } from "@ui/components/common/error";
import { LoadingContainer } from "@ui/components/common/loading-container";
import { useDimensions } from "@ui/hooks/use-dimensions";
import { RoutesPath, TabPatientDetails } from "@ui/navigation/routes";
import { EmptyProgramsPlaceholder } from "@ui/pages/patient-details/components/program-tab/empty-programs-placeholder";
import { ComplianceGraph } from "@ui/pages/patient-details/components/progress-tab/compliance-graph";
import { FeedbackGraph } from "@ui/pages/patient-details/components/progress-tab/feedback-graph";
import { ProgressDataView } from "@ui/pages/patient-details/components/progress-tab/progress-data-view";
import { useAllPatientPrograms } from "@ui/pages/program-builder/hooks/use-patient-program";
import { useProgramActions } from "@ui/pages/program-builder/hooks/use-program-actions";
import { physioMeMobileBreakpoint, row, stack } from "@ui/style";
import { colors } from "@ui/style/colors";
import { isNotNil } from "@utils/assert";
import { truncateString } from "@utils/string-utils";
import { Dropdown } from "primereact/dropdown";
import React, { useCallback, useMemo, useRef } from "react";

export const ProgressTab: React.FC<{ patient: Patient }> = ({ patient, ...otherProps }) => {
	const {
		allPatientPrograms,
		loading,
		error,
		refreshAllPrograms,
		shouldDisplayProgramEndingSoonAlert,
		shouldDisplayProgramEndingNoOngoingAlert,
	} = useAllPatientPrograms(patient);
	const { isMobile } = useDimensions();

	if (!allPatientPrograms && error) {
		return (
			<NetworkError
				style={{ marginTop: 150 }}
				onRefresh={async () => {
					await refreshAllPrograms(patient.id);
				}}
			/>
		);
	}

	return isMobile && loading ? (
		<LoadingContainer />
	) : (
		<ProgressTabContent
			isLoading={allPatientPrograms === undefined && loading}
			patient={patient}
			allPatientPrograms={allPatientPrograms ?? { endedPrograms: [], scheduledPrograms: [], draftPrograms: [] }}
			shouldDisplayProgramEndingSoonAlert={shouldDisplayProgramEndingSoonAlert}
			shouldDisplayProgramEndingNoOngoingAlert={shouldDisplayProgramEndingNoOngoingAlert}
			{...otherProps}
		/>
	);
};

type GroupedPatientPrograms = { label: string; code: ProgramStatus; items: (OngoingProgram | EndedProgram)[] }[];
type GroupedPatientProgramItem = GroupedPatientPrograms[0]["items"][0] | undefined;

const getProgramStatusLabel = (
	program: GroupedPatientProgramItem,
	format: ReturnType<typeof useI18n>["format"],
): string => {
	if (program?.status === ProgramStatus.Ongoing) {
		return format("global.programStatus.ongoing");
	}

	if (program?.status === ProgramStatus.Ended && program.stopped) {
		return format("global.programStatus.stopped");
	}

	return format("global.programStatus.completed");
};

const ProgramValueTemplate: React.FC<{
	program: GroupedPatientProgramItem;
	isMobile: boolean;
	format: ReturnType<typeof useI18n>["format"];
}> = ({ program, isMobile, format }) => {
	const programStatusLabel = getProgramStatusLabel(program, format);

	return (
		<OverflowValue>
			<OverflowText className={isMobile ? "p-size-l-bold" : "p-header-3"}>{program?.name ?? "Undefined"}</OverflowText>
			<span className={isMobile ? "p-size-l" : "p-header-3-regular"} style={{ color: colors.grey80 }}>
				&nbsp;
				{`(${programStatusLabel})`}
			</span>
		</OverflowValue>
	);
};

const ProgressTabContent: React.FC<{
	allPatientPrograms: AllPatientPrograms;
	shouldDisplayProgramEndingSoonAlert: boolean;
	shouldDisplayProgramEndingNoOngoingAlert: boolean;
	isLoading?: boolean;
	patient: Patient;
}> = ({
	patient,
	allPatientPrograms,
	shouldDisplayProgramEndingSoonAlert,
	shouldDisplayProgramEndingNoOngoingAlert,
	isLoading = false,
}) => {
	const { ongoingProgram, endedPrograms, scheduledPrograms } = allPatientPrograms;
	const { programId } = useQueryParams<RoutesPath.PatientDetails>();
	const navigate = useNavigate();
	const { format } = useI18n();
	const { isMobile } = useDimensions();
	const { duplicateAsDraftProgram, loading } = useProgramActions();
	const groupedPatientPrograms: GroupedPatientPrograms = useMemo(
		() => [
			...(ongoingProgram
				? [
						{
							label: format("patientDetails.progress.dropdown.ongoingSection", { count: 1 }),
							code: ProgramStatus.Ongoing,
							items: [ongoingProgram],
						},
					]
				: []),
			...(endedPrograms.length > 0
				? [
						{
							label: format("patientDetails.progress.dropdown.endedSection", { count: endedPrograms.length }),
							code: ProgramStatus.Ended,
							items: endedPrograms,
						},
					]
				: []),
		],
		[format, endedPrograms, ongoingProgram],
	);
	const isEmpty = !isLoading && groupedPatientPrograms.length === 0;
	const hasFuturePrograms = scheduledPrograms.length > 0;
	const skeletonProps = useMemo(() => ({ active: isEmpty || isLoading, loading: isLoading }), [isEmpty, isLoading]);

	const selectedPatientProgram = useMemo(
		() =>
			groupedPatientPrograms.flatMap(programGroup => programGroup.items).find(program => program.id === programId) ??
			(groupedPatientPrograms[0]?.items[0] as PatientProgram | undefined),
		[groupedPatientPrograms, programId],
	);
	const onPatientProgramsChange = (e: { value: PatientProgram | undefined }) => {
		if (e.value) {
			navigate(RoutesPath.PatientDetails, {
				route: { patientId: patient.id },
				query: { active: TabPatientDetails.PROGRESS_TAB, programId: e.value.id },
			});
		}
	};

	// no dropdown methode to blur the dropdown so we focus another item to force the blur
	const hiddenInputRef = useRef<HTMLInputElement>(null);
	const blurDropdown = () => {
		hiddenInputRef.current?.focus();
		hiddenInputRef.current?.blur();
	};

	const extendProgram = useCallback(() => {
		if (selectedPatientProgram?.status === ProgramStatus.Ongoing) {
			sendAnalyticsEventExtendProgram(
				AnalyticsProgramLocation.PROGRESS_TAB,
				patient.analyticsId,
				selectedPatientProgram.id,
			);
			navigate(RoutesPath.ProgramBuilder, {
				route: { patientId: patient.id },
				query: { programId: selectedPatientProgram.id, openScheduleModal: "true" },
			});
		}
	}, [navigate, patient, selectedPatientProgram?.id, selectedPatientProgram?.status]);

	const duplicateAsDraft = useCallback(() => {
		if (isNotNil(selectedPatientProgram)) {
			duplicateAsDraftProgram({
				patientId: patient.id,
				programName: selectedPatientProgram.name,
				programId: selectedPatientProgram.id,
			});
			navigate(RoutesPath.PatientDetails, {
				route: { patientId: patient.id },
				query: { active: TabPatientDetails.PROGRAMS_TAB },
			});
		}
	}, [duplicateAsDraftProgram, navigate, patient.id, selectedPatientProgram]);

	const renderProgramValueTemplate = useCallback(
		(e: GroupedPatientProgramItem) => <ProgramValueTemplate program={e} isMobile={isMobile} format={format} />,
		[format, isMobile],
	);

	const shouldHideContent = isMobile && isEmpty;

	const truncatedPatientName = truncateString(patient.firstName, 20);

	return (
		<Container>
			<AbsoluteHiddenInput ref={hiddenInputRef} />
			{isEmpty && (
				<EmptyProgramsPlaceholder
					patient={patient}
					hasFutureProgram={hasFuturePrograms}
					analyticsLocation={AnalyticsProgramLocation.PROGRESS_TAB}
					style={{ position: shouldHideContent ? "static" : "absolute" }}
				/>
			)}
			{!shouldHideContent && (
				<>
					<ButtonContainer>
						<ConditionalSkeleton {...skeletonProps} skeletonStyle={{ borderRadius: "0.5rem" }}>
							<StyledDropdown
								scrollHeight="350px"
								value={selectedPatientProgram}
								style={skeletonProps.active ? { width: 350 } : undefined}
								options={groupedPatientPrograms}
								onChange={onPatientProgramsChange}
								optionLabel="name"
								onHide={blurDropdown}
								optionGroupLabel="label"
								optionGroupChildren="items"
								valueTemplate={renderProgramValueTemplate}
							/>
						</ConditionalSkeleton>

						<ConditionalSkeleton {...skeletonProps} skeletonStyle={{ borderRadius: "0.5rem" }}>
							<Button
								label={format("patientDetails.progress.openProgramButton")}
								onClick={() => {
									if (!selectedPatientProgram) return;
									navigate(RoutesPath.ProgramBuilder, {
										route: { patientId: patient.id },
										query: { programId: selectedPatientProgram.id },
									});
									sendAnalyticsEventViewProgram(
										patient.analyticsId,
										selectedPatientProgram,
										TabPatientDetails.PROGRESS_TAB,
									);
								}}
							/>
						</ConditionalSkeleton>
					</ButtonContainer>
					{shouldDisplayProgramEndingSoonAlert && (
						<AlertCard
							title={format("patientDetails.alert.programEndingSoon.title", { patientName: truncatedPatientName })}
							message={format("patientDetails.alert.programEndingSoon.message")}
							cardType={CardType.Warn}
							secondaryButtons={[
								{
									title: format("patientDetails.alert.programEndingSoon.button"),
									onPressed: extendProgram,
								},
							]}
						/>
					)}
					{shouldDisplayProgramEndingNoOngoingAlert && (
						<AlertCard
							title={format("patientDetails.alert.programEnded.title", { patientName: truncatedPatientName })}
							message={format("patientDetails.alert.programEnded.message")}
							cardType={CardType.Warn}
							secondaryButtons={[
								{
									title: format("patientDetails.alert.programEnded.button"),
									onPressed: duplicateAsDraft,
									loading: loading,
								},
							]}
						/>
					)}
					<ProgressDataView
						isEmpty={isEmpty}
						isLoading={isLoading}
						patientId={patient.id}
						programId={selectedPatientProgram?.id}
						programStatus={selectedPatientProgram?.status}
					/>
					<ComplianceGraph
						isEmpty={isEmpty}
						isLoading={isLoading}
						patientId={patient.id}
						programId={selectedPatientProgram?.id}
					/>
					<FeedbackGraph
						isEmpty={isEmpty}
						isLoading={isLoading}
						patientId={patient.id}
						programId={selectedPatientProgram?.id}
					/>
				</>
			)}
		</Container>
	);
};

const Container = styled.div`
	${stack(40, "center", "stretch")};
	flex-grow: 1;
	position: relative;
`;

const ButtonContainer = styled.div`
	${row(undefined, "spacebetween", "center")};

	@media ${physioMeMobileBreakpoint} {
		${stack(8, "spacebetween", "flexstart")};
	}
`;

const StyledDropdown = styled(Dropdown)`
	max-width: calc(100% - 10rem);
	margin-left: -1rem;

	background-color: ${colors.white};

	.p-dropdown-trigger-icon {
		font-size: 1.25rem;
	}

	.p-dropdown-label {
		padding: 1rem 0 1rem 1rem;
	}

	.p-dropdown-panel .p-dropdown-items {
		.p-dropdown-item-group {
			padding-left: 1rem;
		}

		.p-dropdown-item {
			padding-left: 2.5rem;

			&.p-highlight:before {
				left: 1rem;
			}
		}
	}

	:not(.p-disabled).p-focus {
		background-color: ${colors.primaryBackground};
	}

	:hover:not(.p-focus) {
		background-color: ${colors.primaryBackground};
		border-color: ${colors.primaryBackground} !important;
	}

	@media ${physioMeMobileBreakpoint} {
		max-width: 105%;
	}
`;

const OverflowValue = styled.span`
	width: 100%;
	display: flex;
	flex-direction: row;
	overflow: hidden;
`;

const OverflowText = styled.span`
	display: block;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

const AbsoluteHiddenInput = styled.input`
	position: absolute;
	opacity: 0;
	top: 0;
`;
